import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import {
  ActivatedRoute,
  CanActivateFn,
  NavigationEnd,
  Router
} from '@angular/router';
import { of, Subject } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  startWith,
  switchMap,
  takeUntil,
  tap
} from 'rxjs/operators';
import { FeatureKey } from 'src/app/_models';
import { BusinessServices } from '../../businesses/services/business.services';
import { FeaturesBaseServices } from '../../features/services/features-base.services';
import { BaseServices } from '../../kuba.services';

/** Hosts safety-round routes beneath the Flexi menu. */
@Component({
  selector: 'flexi-safety-round-workspace',
  standalone: false,
  template: `
    <nav
      class="fvr-workspace-tabs"
      [attr.aria-label]="'FLEXI_SAFETY_ROUNDS.TAB' | translate"
    >
      <a
        [routerLink]="['./']"
        routerLinkActive="is-active"
        [routerLinkActiveOptions]="{ exact: true }"
        ariaCurrentWhenActive="page"
      >
        {{ 'FLEXI_SAFETY_ROUNDS.OVERVIEW.TAB' | translate }}
      </a>
      <a
        *ngIf="canSeeDeviations"
        [routerLink]="['deviations']"
        routerLinkActive="is-active"
        ariaCurrentWhenActive="page"
      >
        {{ 'FLEXI_SAFETY_ROUNDS.DEVIATIONS.TAB' | translate }}
      </a>
    </nav>
    <router-outlet></router-outlet>
  `,
  styleUrls: ['./safety-round-workspace.component.scss']
})
export class SafetyRoundWorkspaceComponent implements OnInit, OnDestroy {
  canSeeDeviations = false;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private business: BusinessServices,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        startWith(null),
        map(
          () =>
            Number(
              this.route.snapshot.pathFromRoot
                .find(parent => parent.routeConfig?.path === 'business/:id')
                ?.paramMap.get('id')
            ) || BaseServices.BusinessId
        ),
        distinctUntilChanged(),
        tap(() => (this.canSeeDeviations = false)),
        switchMap(businessId =>
          this.business.getById(businessId).pipe(
            map(business => canUseSafetyRoundDeviations(business)),
            catchError(() => of(false))
          )
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(allowed => (this.canSeeDeviations = allowed));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}

/** Check the local Flexi branch, rather than another module's shared Deviation child. */
export function canUseSafetyRoundDeviations(business: any): boolean {
  const context = {
    ...BaseServices.getUserFeatures(),
    Features: business.Features,
    Type: 'business',
    Details: business
  };
  const flexi = FeaturesBaseServices.getFeatureDetails(context)?.find(
    f => f.id === FeatureKey.FLEXI
  );
  const rounds = flexi?.children?.find(
    f => f.id === FeatureKey.FLEXI_SAFETY_ROUNDS
  );
  const deviations = rounds?.children?.find(
    f => f.id === FeatureKey.DEVIATIONS
  );
  return (
    flexi?.checked === true &&
    flexi?.access !== false &&
    rounds?.checked === true &&
    rounds?.access !== false &&
    deviations?.checked !== false &&
    deviations?.access !== false
  );
}

/** Also enforce the feature selection on direct links. API authorization remains authoritative. */
export const safetyRoundDeviationsGuard: CanActivateFn = (route, state) => {
  const router = inject(Router);
  const fallback = router.parseUrl(
    `${state.url.split('/Flexi')[0]}/Flexi/safety-rounds`
  );
  const routedBusinessId = Number(
    route.pathFromRoot
      .find(parent => parent.routeConfig?.path === 'business/:id')
      ?.paramMap.get('id')
  );
  const businessId =
    routedBusinessId > 0 ? routedBusinessId : BaseServices.BusinessId;
  return inject(BusinessServices)
    .getById(businessId)
    .pipe(
      map(business =>
        canUseSafetyRoundDeviations(business) ? true : fallback
      ),
      catchError(() => of(fallback))
    );
};
